import { usePotholeRepairsKPI, useStateHighwaysPotholeRepairsKPI } from '@src/modules/performance/context/reports-api-hooks';
import { selectedDatabaseIdState } from '@transport-insights/uikit';
import { TRAFFIC_LIGHT_COLOURS as COLOURS, stateHighwaysPotholeRepairsLegend } from '@src/modules/performance/shared/constants';
import { Text } from '@chakra-ui/react';
import { useRecoilValue } from 'recoil';
import Widget from './Widget';
import { CircleIndicator } from '../indicators';
import { mapValueToColourRange, roundKpiValue } from '../../shared/helpers';
import IndicatorLegend from '../IndicatorLegend';
import { selectedRegionRoadTypeState } from '../../state';

export default function PotholeRepairsWidget() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const selectedRegionRoadType = useRecoilValue(selectedRegionRoadTypeState);
  const { data: rcaData, isLoading: isLoadingRca, isError: isErrorRca } = usePotholeRepairsKPI();
  const { data: stateHighwaysPotholeRepairsData, isLoading: isLoadingSh, isError: isErrorSh } = useStateHighwaysPotholeRepairsKPI();
  const isLoading = isLoadingRca || isLoadingSh;
  const isError = isErrorRca || isErrorSh;
  const isStateHighways = rcaId === 81;
  const isRegionStateHighway = rcaId >= 1000 && selectedRegionRoadType === 'sh';

  // Exclude Doc Roads
  if (rcaId === 88) {
    return null;
  }

  let data = null;
  const title = 'Pothole Repairs';
  let description = null;
  let moreInfoContent = null;
  let reportLink = null;
  let colour = null;

  if (isStateHighways) {
    data = stateHighwaysPotholeRepairsData;
    description = '% completed within 24 hours';
    colour = mapValueToColourRange(data?.percentAchievedYtd, 'stateHighwaysPotholeRepairsKpi');
    moreInfoContent = (
      <>
        <Text fontSize="sm" mb={4}>
          The year to date percentage of potholes on all State Highways that were repaired within 24 hours, to the end of the quarter.
        </Text>
        <IndicatorLegend items={stateHighwaysPotholeRepairsLegend} />
      </>
    );
    reportLink = '/performance/dashboard/state-highways-pothole-repairs';
  } else if (isRegionStateHighway) {
    data = rcaData;
    description = '% completed within 24 hours';
    colour = mapValueToColourRange(rcaData?.value, 'stateHighwaysPotholeRepairsKpi');
    moreInfoContent = (
      <>
        <Text fontSize="sm" mb={4}>
          The year to date percentage of potholes on all State Highways that were repaired within 24 hours, to the end of the quarter.
        </Text>
        <IndicatorLegend items={stateHighwaysPotholeRepairsLegend} />
      </>
    );
    reportLink = null;
  } else {
    data = rcaData;
    description = 'Proportion repaired year to date (from Q3\n24/25) within the RCA’s Levels of Service';
    moreInfoContent = 'The percentage of the potholes that were repaired in the year to date starting Q3 24/25, within the Levels of Service response timeframe defined by the RCA.';
    reportLink = null;
  }

  // If there is an error we want to hide the widget
  if (isError) return null;

  return (
    <Widget
      link={reportLink}
      title={title}
      description={description}
      lockYear={data?.lockYear}
      quarter={data?.quarter}
      quarterFormat="ytd"
      isLoading={isLoading && !data}
      moreInfoContent={(
        <Text fontSize="sm">
          {moreInfoContent}
        </Text>
      )}
    >
      {isStateHighways ? (
        <CircleIndicator
          size={100}
          colour={COLOURS[colour]}
          value={data?.percentAchievedYtd !== null && data?.percentAchievedYtd !== undefined ? `${roundKpiValue(data?.percentAchievedYtd, 5, 1)}%` : null}
          isLoading={isLoading}
          padding={0}
        />
      ) : (
        <CircleIndicator
          size={100}
          colour={colour ? COLOURS[colour] : 'gray.700'}
          value={data?.value !== null && data?.value !== undefined ? `${roundKpiValue(data?.value, 5, 1)}%` : null}
          isLoading={isLoading}
          padding={0}
        />
      )}
    </Widget>
  );
}
